import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import TgEmoji, { unifiedToNative } from './TgEmoji'
import emojiData from 'emoji-datasource-apple/emoji.json'
import type { LucideIcon } from 'lucide-react'
import { Search, Clock, Smile, User, PawPrint, Apple, Plane, Trophy, Lightbulb, Hash, Flag } from 'lucide-react'

interface Props {
  /** Called with the native emoji string (e.g. "😂") on selection. */
  onSelect: (emoji: string) => void
  /** Display size for the grid emoji (32 = compact, 36 = roomy). */
  cellSize?: number
}

interface EmojiRecord {
  unified: string
  short_name: string
  name: string
  sheet_x: number
  sheet_y: number
  category: string
  sort_order: number
  keywords: string[]
}

const RECENT_KEY = 'rx2go.tg-emoji.recent.v1'
const MAX_RECENT = 36

// Telegram-style category order + icons. "Component" is hidden — those
// are skin-tone modifiers, not standalone emoji.
const CATEGORY_ORDER: { name: string; label: string; icon: LucideIcon }[] = [
  { name: 'Smileys & Emotion', label: 'Smileys', icon: Smile },
  { name: 'People & Body',     label: 'People',  icon: User },
  { name: 'Animals & Nature',  label: 'Nature',  icon: PawPrint },
  { name: 'Food & Drink',      label: 'Food',    icon: Apple },
  { name: 'Travel & Places',   label: 'Travel',  icon: Plane },
  { name: 'Activities',        label: 'Activity', icon: Trophy },
  { name: 'Objects',           label: 'Objects', icon: Lightbulb },
  { name: 'Symbols',           label: 'Symbols', icon: Hash },
  { name: 'Flags',             label: 'Flags',   icon: Flag },
]

// One-time index build: filter out emoji without Apple sprite +
// "Component" (skin tones), normalise the keyword list once, and
// bucket by category preserving sort_order.
function buildIndex(): {
  byCategory: Record<string, EmojiRecord[]>
  flat: EmojiRecord[]
} {
  const flat: EmojiRecord[] = []
  for (const e of emojiData as any[]) {
    if (!e.has_img_apple) continue
    if (e.category === 'Component') continue
    flat.push({
      unified: String(e.unified),
      short_name: String(e.short_name),
      name: String(e.name || ''),
      sheet_x: e.sheet_x,
      sheet_y: e.sheet_y,
      category: e.category,
      sort_order: e.sort_order,
      keywords: Array.isArray(e.short_names) ? e.short_names : [e.short_name],
    })
  }
  flat.sort((a, b) => a.sort_order - b.sort_order)
  const byCategory: Record<string, EmojiRecord[]> = {}
  for (const cat of CATEGORY_ORDER) byCategory[cat.name] = []
  for (const e of flat) {
    if (byCategory[e.category]) byCategory[e.category].push(e)
  }
  return { byCategory, flat }
}

const INDEX = buildIndex()

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.slice(0, MAX_RECENT).filter((x) => typeof x === 'string')
  } catch {
    return []
  }
}

function saveRecent(unifieds: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(unifieds.slice(0, MAX_RECENT)))
  } catch {
    // ignore quota errors
  }
}

export default function TgEmojiPicker({ onSelect, cellSize = 32 }: Props) {
  const [query, setQuery] = useState('')
  const [recent, setRecent] = useState<string[]>(() => loadRecent())
  const scrollRef = useRef<HTMLDivElement>(null)
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [activeCategory, setActiveCategory] = useState<string>('recent')

  const recentRecords = useMemo(
    () => recent.map((u) => INDEX.flat.find((e) => e.unified === u)).filter(Boolean) as EmojiRecord[],
    [recent]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return null
    return INDEX.flat.filter(
      (e) =>
        e.short_name.includes(q) ||
        e.name.toLowerCase().includes(q) ||
        e.keywords.some((k) => k.includes(q))
    ).slice(0, 200)
  }, [query])

  const handlePick = useCallback(
    (e: EmojiRecord) => {
      onSelect(unifiedToNative(e.unified))
      setRecent((prev) => {
        const next = [e.unified, ...prev.filter((u) => u !== e.unified)].slice(0, MAX_RECENT)
        saveRecent(next)
        return next
      })
    },
    [onSelect]
  )

  // Highlight the category whose section is currently in view as the
  // user scrolls — same affordance Telegram uses.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = () => {
      const top = el.scrollTop + 8
      let current = recent.length > 0 ? 'recent' : (CATEGORY_ORDER[0]?.name || '')
      for (const cat of CATEGORY_ORDER) {
        const sec = sectionRefs.current[cat.name]
        if (!sec) continue
        if (sec.offsetTop - el.offsetTop <= top) current = cat.name
      }
      setActiveCategory((prev) => (prev === current ? prev : current))
    }
    el.addEventListener('scroll', handler)
    handler()
    return () => el.removeEventListener('scroll', handler)
  }, [recent.length, query])

  const scrollToCategory = (key: string) => {
    const el = scrollRef.current
    if (!el) return
    if (key === 'recent') {
      el.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    const sec = sectionRefs.current[key]
    if (sec) {
      el.scrollTo({ top: sec.offsetTop - el.offsetTop, behavior: 'smooth' })
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-3 pt-3 pb-2 shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-muted rounded-lg outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* Grid */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-2 pb-2">
        {filtered ? (
          <Section
            title={`Results (${filtered.length})`}
            items={filtered}
            onPick={handlePick}
            cellSize={cellSize}
          />
        ) : (
          <>
            {recentRecords.length > 0 && (
              <Section
                title="Recently used"
                items={recentRecords}
                onPick={handlePick}
                cellSize={cellSize}
              />
            )}
            {CATEGORY_ORDER.map((cat) => (
              <div
                key={cat.name}
                ref={(el) => { sectionRefs.current[cat.name] = el }}
              >
                <Section
                  title={cat.label}
                  items={INDEX.byCategory[cat.name] || []}
                  onPick={handlePick}
                  cellSize={cellSize}
                />
              </div>
            ))}
          </>
        )}
        {filtered && filtered.length === 0 && (
          <div className="text-center text-sm text-muted-foreground mt-6">No emoji found</div>
        )}
      </div>

      {/* Bottom nav — Telegram-style category strip */}
      <div className="shrink-0 flex items-center gap-1 border-t border-border px-2 py-1.5 overflow-x-auto">
        {recentRecords.length > 0 && (
          <NavTab
            active={activeCategory === 'recent' && !query}
            onClick={() => scrollToCategory('recent')}
            icon={<Clock size={16} />}
            title="Recently used"
          />
        )}
        {CATEGORY_ORDER.map((cat) => (
          <NavTab
            key={cat.name}
            active={activeCategory === cat.name && !query}
            onClick={() => scrollToCategory(cat.name)}
            icon={<cat.icon size={16} />}
            title={cat.label}
          />
        ))}
      </div>
    </div>
  )
}

function Section({
  title,
  items,
  onPick,
  cellSize,
}: {
  title: string
  items: EmojiRecord[]
  onPick: (e: EmojiRecord) => void
  cellSize: number
}) {
  if (items.length === 0) return null
  return (
    <div className="mt-3 first:mt-1">
      <div className="px-2 mb-1 text-[11px] uppercase tracking-wide font-medium text-muted-foreground">
        {title}
      </div>
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(auto-fill, minmax(${cellSize + 6}px, 1fr))`,
          gap: 2,
        }}
      >
        {items.map((e) => (
          <button
            key={e.unified}
            onClick={() => onPick(e)}
            className="flex items-center justify-center rounded hover:bg-accent transition p-1"
            title={e.short_name}
          >
            <TgEmoji
              unified={e.unified}
              sheetX={e.sheet_x}
              sheetY={e.sheet_y}
              size={cellSize - 4}
            />
          </button>
        ))}
      </div>
    </div>
  )
}

function NavTab({
  active,
  onClick,
  icon,
  title,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  title: string
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`shrink-0 w-8 h-8 flex items-center justify-center rounded transition ${
        active
          ? 'text-primary bg-primary/10'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
      }`}
    >
      {icon}
    </button>
  )
}
